import React, { useEffect, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';

import { useEmployeePatoReport } from '../../hooks/useQueries';

// One employee's PATO for the calendar year as a ledger: the company grant the
// year started with, then every policy change and adjustment that credited or
// debited it and every booking against the time off or sick time task that drew
// it down, each with what was left after it. One hours column carries both:
// usage as a minus, an adjustment or policy change as the signed amount it
// credited or debited. Sick time is PATO too; its rows are marked so it can be
// told apart from time off.
//
// The figures and the running balance come from the server (GET
// /payroll/pato/:id/report), from the same ledger the PATO balance on the
// employee record reads. Under unlimited time off there is no balance, so that
// column says so.

// Parsed off the date-only string rather than through new Date(iso), which
// would read it as UTC midnight and show the day before west of Greenwich.
const formatDate = (iso) => {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatHours = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

const formatSigned = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(2)}`;
};

// Marks a row by what it is - sick time, an adjustment, a policy change - so
// each can be told apart from plain time off.
const CHIP_STYLE = {
  display: 'inline-block',
  marginRight: 6,
  padding: '0 6px',
  borderRadius: 4,
  background: 'var(--bdc-surface-sunk, #eceef0)',
  color: 'var(--bdc-text-muted, #45474c)',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: '16px',
  verticalAlign: 'baseline',
};

// The two figure columns get a fixed floor and never wrap: a long description
// would otherwise squeeze them until "-123.75" broke across two lines.
const FIGURE_COLUMN_STYLE = { width: 120, minWidth: 120, whiteSpace: 'nowrap' };
const FIGURE_CELL_STYLE = { whiteSpace: 'nowrap' };
// Wide enough for the longest date, "Sep 30, 2026", on one line.
const DATE_COLUMN_STYLE = { width: 130, minWidth: 130, whiteSpace: 'nowrap' };

// A mid-year policy change moves everybody's balance on its date, so to the
// person reading their ledger it is an adjustment like any other.
const CHIP_LABEL = {
  sick: 'Sick',
  adjustment: 'Adjustment',
  policy: 'Adjustment',
};

const DEFAULT_DESCRIPTION = {
  sick: 'Sick time',
  pato: 'Time off',
  adjustment: 'Adjustment',
};

export default function PatoReportModal({ open, onClose, employee: employeeProp }) {
  const employee = useLastPresent(employeeProp); // keeps the dialog steady while it fades out
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  // Every opening starts on this year, whoever it is opened for - a past year
  // left over from the last person looked at would be easy to misread as theirs.
  useEffect(() => {
    if (open) setYear(new Date().getFullYear());
  }, [open, employee?.id]);

  // Stops at this year: a future year has nothing booked against it yet and
  // would only show an allowance nobody has set.
  const canGoForward = year < currentYear;

  const query = useEmployeePatoReport(employee?.id, year, { enabled: Boolean(open && employee?.id) });
  const report = query.data;
  const name = `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim() || employee?.email || '';
  const balanceClass = (value) => `nsi-table__right bdc-num${Number(value) < 0 ? ' bdc-text-bad' : ''}`;
  const balanceText = (event) => {
    if (event.balanceAfter != null) return formatHours(event.balanceAfter);
    return event.unlimited ? 'Unlimited' : '—';
  };

  const events = report?.events || [];
  // Everything after the opening grant. The grant is always there, so a year
  // with nothing else in it is a year with nothing taken or adjusted.
  const activity = events.slice(1);

  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>PATO report &mdash; {name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          className="d-flex align-items-center justify-content-center gap-3"
          style={{ marginBottom: 16 }}
        >
          <Button size="sm" icon="chevron-left" onClick={() => setYear((y) => y - 1)} aria-label="Previous year" />
          <span className="bdc-num" style={{ fontSize: 18, fontWeight: 600, minWidth: 56, textAlign: 'center' }}>
            {year}
          </span>
          <Button
            size="sm"
            icon="chevron-right"
            onClick={() => setYear((y) => y + 1)}
            disabled={!canGoForward}
            aria-label="Next year"
          />
        </div>

        {query.isLoading ? (
          <Loader content="Loading PATO report…" />
        ) : query.isError ? (
          <Message type="error" showIcon>
            {query.error?.message || 'The PATO report could not be loaded.'}
          </Message>
        ) : report ? (
          <>
            {report.usageKnown === false ? (
              <Message type="warning" showIcon style={{ marginBottom: 12 }}>
                Only the grant and adjustments are shown, without a running balance.{' '}
                {report.unavailableReason || 'TimeCamp could not be read.'}
              </Message>
            ) : null}

            <table className="nsi-table">
              <thead>
                <tr>
                  <th style={DATE_COLUMN_STYLE}>Date</th>
                  <th>Description</th>
                  <th className="nsi-table__right" style={FIGURE_COLUMN_STYLE}>Used</th>
                  <th className="nsi-table__right" style={FIGURE_COLUMN_STYLE}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => {
                  const isOpening = index === 0;
                  const chip = CHIP_LABEL[event.kind];
                  return (
                    <tr key={event.id || `${event.kind}-${event.date}-${index}`}>
                      <td className="bdc-num" style={FIGURE_CELL_STYLE}>{formatDate(event.date)}</td>
                      <td className="bdc-table-wrap">
                        {chip ? <span style={CHIP_STYLE}>{chip}</span> : null}
                        {isOpening ? (
                          <strong>{event.description}</strong>
                        ) : (
                          event.description || DEFAULT_DESCRIPTION[event.kind] || ''
                        )}
                      </td>
                      {/* Usage as a minus, adjustments and policy changes as
                          the signed amount they moved the balance by. The
                          grant row's description already says its hours. */}
                      <td
                        className={`nsi-table__right bdc-num${Number(event.credit) < 0 ? ' bdc-text-bad' : ''}`}
                        style={FIGURE_CELL_STYLE}
                      >
                        {event.hours != null
                          ? <>&minus;{formatHours(event.hours)}</>
                          : !isOpening && event.credit != null ? formatSigned(event.credit) : ''}
                      </td>
                      <td className={balanceClass(event.balanceAfter)} style={FIGURE_CELL_STYLE}>
                        {balanceText(event)}
                      </td>
                    </tr>
                  );
                })}

                {report.usageKnown !== false && activity.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="nsi-table__empty">
                      {year === currentYear
                        ? 'No PATO or sick time taken yet this year.'
                        : `No PATO or sick time taken in ${year}.`}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              {report.usageKnown !== false ? (
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      {year} totals &middot; {report.unlimited ? 'unlimited time off' : 'remaining'}
                    </td>
                    <td className="nsi-table__right bdc-num" style={FIGURE_CELL_STYLE}>
                      &minus;{formatHours(report.usedHours)}
                    </td>
                    <td className={balanceClass(report.remainingHours)} style={FIGURE_CELL_STYLE}>
                      {report.unlimited ? 'Unlimited' : formatHours(report.remainingHours)}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>

            <div className="text-muted small" style={{ marginTop: 12 }}>
              Deductions are the hours booked against the paid time off and sick time tasks in TimeCamp.
              Adjustments and company policy changes show the hours they credited (+) or debited (&minus;).
              Hours in the pay period in progress can still change until payroll has run for it.
              {report.through && year === currentYear
                ? ` Items dated after ${formatDate(report.through)} are not counted until their date.`
                : ''}
            </div>
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
